/**
 * Création de compte et connexion — un seul écran, la porte de /compte.
 *
 * Bref formulaire d'inscription avec mot de passe (depuis 0015), « j'ai déjà un
 * compte » en dessous, et le lien par e-mail en secours. L'entreprise se
 * complète ensuite dans l'espace : la friction d'inscription reste l'ennemi
 * n°1 (ADR-012).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { envoyer } from '../reseau.ts';
import { Marque } from '../composants/Marque.tsx';

/**
 * La porte de /compte (Tom, 2026-09-14) : le bref formulaire d'inscription tout
 * de suite — prénom, nom, e-mail, mot de passe, numéro de TVA facultatif — et
 * « j'ai déjà un compte » juste en dessous pour entrer avec e-mail et mot de
 * passe. Le lien par e-mail reste le secours (« mot de passe oublié »).
 *
 * Un compte qui existe déjà ne se recrée pas : l'API répond dejaExistant sans
 * ouvrir de session, et on bascule sur la connexion.
 */
export function CompteCreer() {
  return <CompteConnexion modeInitial="inscription" />;
}

export function CompteConnexion({ modeInitial = 'inscription' }: { modeInitial?: 'inscription' | 'connexion' }) {
  const naviguer = useNavigate();
  const [mode, setMode] = useState<'inscription' | 'connexion' | 'lien'>(modeInitial);
  // Le code de parrainage arrive dans l'URL (`?p=AB3K7M`) : lien partagé ou QR
  // scanné. On le garde pour l'inscription, sans le montrer.
  const [parrain] = useState(() => new URLSearchParams(location.search).get('p')?.trim() || undefined);
  // Venu d'une fiche événement (« J'y participe ») : on y retourne inscrit.
  const [evenement] = useState(() => new URLSearchParams(location.search).get('evenement')?.trim() || null);
  const destination = evenement ? `/evenements/${encodeURIComponent(evenement)}?rejoindre=1` : null;
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [numeroTva, setNumeroTva] = useState('');

  // Un lien reçu par e-mail arrive avec ?jeton=… : on l'échange sans rien demander.
  useEffect(() => {
    const jetonUrl = new URLSearchParams(location.search).get('jeton');
    if (!jetonUrl) return;
    let annule = false;
    void envoyer('/api/compte/connexion', { jeton: jetonUrl }).then((r) => {
      if (annule) return;
      if (r.erreur) {
        setErreur(`${r.erreur} — connectez-vous ci-dessous ou demandez un nouveau lien.`);
        setMode('connexion');
      } else naviguer('/compte', { replace: true });
    });
    return () => {
      annule = true;
    };
  }, [naviguer]);

  const inscriptionValide = prenom.trim().length > 0 && email.trim().includes('@') && motDePasse.length >= 8;
  const connexionValide = email.trim().includes('@') && motDePasse.length > 0;

  const inscrire = async () => {
    setEnCours(true);
    setErreur(null);
    const r = await envoyer<{ ok: boolean; dejaExistant: boolean }>('/api/compte/creer', {
      email: email.trim(),
      prenom: prenom.trim(),
      nom: nom.trim() || undefined,
      motDePasse,
      numeroTva: numeroTva.trim() || undefined,
      parrain,
      origineParrainage: parrain ? 'lien' : undefined,
      evenement: evenement ?? undefined,
    });
    setEnCours(false);
    if (r.erreur) return setErreur(r.erreur);
    if (r.donnees?.dejaExistant) {
      setMode('connexion');
      setInfo('Un compte existe déjà pour cette adresse : connectez-vous.');
      return;
    }
    // Le compte est ouvert : d'abord la photo (pour qu'on vous reconnaisse),
    // puis l'événement d'où l'on vient, sinon l'entreprise.
    naviguer(`/compte/photo?suite=${encodeURIComponent(destination ?? '/compte/entreprise')}`, { replace: true });
  };

  const connecter = async () => {
    setEnCours(true);
    setErreur(null);
    const r = await envoyer('/api/compte/connexion-mot-de-passe', { email: email.trim(), motDePasse });
    setEnCours(false);
    if (r.erreur) return setErreur(r.erreur);
    if (destination) location.assign(destination);
    else naviguer('/compte', { replace: true });
  };

  const demanderLien = async () => {
    setEnCours(true);
    await envoyer('/api/compte/lien-connexion', { email: email.trim() });
    setEnCours(false);
    setInfo('Si un compte existe pour cette adresse, le lien de connexion vient d’y être envoyé.');
  };

  return (
    <Page>
      <h1 className="display-title text-[30px] text-blanc sm:text-[40px]">
        {mode === 'inscription' ? (
          <>Créez votre <Gradient>compte</Gradient></>
        ) : (
          <>Mon <Gradient>compte</Gradient></>
        )}
      </h1>
      <p className="mt-3 max-w-[520px] text-[15px] leading-relaxed text-fumee">
        {mode === 'inscription'
          ? 'Une minute. Votre compte vous suit d’un événement à l’autre, et vous complétez votre entreprise dans votre espace.'
          : mode === 'connexion'
            ? 'Entrez avec votre e-mail et votre mot de passe.'
            : 'Indiquez votre e-mail : nous vous envoyons un lien de connexion.'}
      </p>

      {info && <p className="mt-4 text-[14px] text-blanc">{info}</p>}

      <div className="mt-6 flex flex-col gap-4">
        {mode === 'inscription' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ label="Prénom" value={prenom} onChange={setPrenom} placeholder="Ada" autoFocus />
            <Champ label="Nom" value={nom} onChange={setNom} placeholder="Lovelace" />
          </div>
        )}
        <Champ label="E-mail professionnel" value={email} onChange={setEmail} type="email" placeholder="ada@monentreprise.be" autoFocus={mode !== 'inscription'} />
        {mode !== 'lien' && (
          <Champ
            label={mode === 'inscription' ? 'Mot de passe (8 caractères min.)' : 'Mot de passe'}
            value={motDePasse}
            onChange={setMotDePasse}
            type="password"
            placeholder="••••••••"
          />
        )}
        {mode === 'inscription' && (
          <Champ label="Numéro de TVA (facultatif)" value={numeroTva} onChange={setNumeroTva} placeholder="BE 0123.456.789" />
        )}

        {erreur && (
          <p className="text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>
        )}

        {mode === 'inscription' && (
          <button className="btn-creme mt-2 self-start" disabled={!inscriptionValide || enCours} onClick={() => void inscrire()}>
            {enCours ? '…' : 'Créer mon compte'} <ArrowRight size={15} strokeWidth={1.5} className="ml-1.5 inline" />
          </button>
        )}
        {mode === 'connexion' && (
          <button className="btn-creme mt-2 self-start" disabled={!connexionValide || enCours} onClick={() => void connecter()}>
            {enCours ? '…' : 'Me connecter'} <ArrowRight size={15} strokeWidth={1.5} className="ml-1.5 inline" />
          </button>
        )}
        {mode === 'lien' && (
          <button className="btn-creme mt-2 self-start" disabled={!email.includes('@') || enCours} onClick={() => void demanderLien()}>
            {enCours ? '…' : 'Recevoir mon lien'}
          </button>
        )}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-graphite pt-5 text-[14px]">
        {mode !== 'inscription' && (
          <button onClick={() => { setMode('inscription'); setErreur(null); setInfo(null); }} className="text-fumee hover:text-blanc">
            Créer un compte
          </button>
        )}
        {mode !== 'connexion' && (
          <button onClick={() => { setMode('connexion'); setErreur(null); setInfo(null); }} className="text-fumee hover:text-blanc">
            J’ai déjà un compte
          </button>
        )}
        {mode !== 'lien' && (
          <button onClick={() => { setMode('lien'); setErreur(null); setInfo(null); }} className="text-fumee hover:text-blanc">
            Mot de passe oublié ? Recevoir un lien
          </button>
        )}
        <a href="/" className="ml-auto text-fumee hover:text-blanc">Retour au site</a>
      </div>

      <p className="mt-6 text-[12px] leading-relaxed text-fumee">
        Vos données sont hébergées dans l’Union européenne. Vous pouvez masquer votre profil de
        l’annuaire ou fermer votre compte à tout moment depuis votre espace.
      </p>
    </Page>
  );
}

function Page({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-void px-4 py-10 font-sans text-blanc sm:px-8">
      <div className="mx-auto max-w-[640px]">
        <a href="/" className="mb-10 flex items-center gap-2.5" aria-label="EventMatch.pro">
          <Marque />
          <span className="text-[15px] font-medium text-blanc">
            EventMatch<span className="text-fumee">.pro</span>
          </span>
        </a>
        {children}
      </div>
    </div>
  );
}

function Gradient({ children }: { children: ReactNode }) {
  return <em className="text-match-gradient font-serif italic font-medium">{children}</em>;
}

function Champ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoFocus = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="block">
      <span className="label-caps block text-fumee">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={type === 'password' ? 'current-password' : type === 'email' ? 'email' : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="field-input mt-2"
      />
    </label>
  );
}
